// Die Fehlergrenze des Anwendungsrahmens (ST-UX-001, NFR-UX-005).
//
// Ohne sie nimmt ein einziger Renderfehler die GANZE Oberflaeche mit: React
// haengt den Baum aus, und zurueck bleibt eine weisse Seite ohne Text, ohne
// Navigation und ohne Hinweis. Fuer jemanden ohne technische Vorkenntnisse ist
// eine weisse Seite nicht "ein Fehler", sondern "ich habe etwas kaputtgemacht"
// -- und CM-UX-010 verlangt ausdruecklich Verstaendlichkeit ohne Fachwissen.
//
// Eine KLASSE, obwohl der Rest der Oberflaeche mit Funktionen arbeitet: React
// bietet `componentDidCatch` bis heute nur so an. Das ist kein Stilbruch,
// sondern die einzige Form, die es gibt.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Card,
} from '@fluentui/react-components';

import { useScreenStyles } from './screen-styles.ts';
import { Zustand } from './zustand.tsx';

/**
 * Die Absturzmeldung, mit EIGENEM Text.
 *
 * Der erste Entwurf hat hier den Zugangsbildschirm wiederverwendet und ihm die
 * Meldung untergeschoben. Das war bequem und falsch: dessen Text waehlt seinen
 * naechsten Schritt danach, ob die Meldung mit "LIAISON" beginnt, und haette
 * einem Absturz den Rat "Melde dich neu an" verpasst. Eine Anmeldung repariert
 * keinen Renderfehler -- der Rat haette Arbeit gekostet und nichts geloest.
 *
 * Aufbau wie jede Ablehnung der Anwendung: Problem, Wirkung, naechster Schritt.
 * Ein Absturz ist fuer die Nutzerin kein anderer Vorgang als eine Ablehnung --
 * beide Male geht es gerade nicht weiter, und beide Male will sie wissen, was
 * sie tun kann. Nur die Ursache unterscheidet sich, und die interessiert sie
 * nicht.
 */
function CrashMessage({ detail }: { readonly detail: string | undefined }): ReactNode {
  const styles = useScreenStyles();
  return (
    <Card className={styles.card}>
      <Zustand
        art="fehler"
        titel="Diese Ansicht konnte nicht angezeigt werden"
        saetze={[
          'In LIAISON ist ein Fehler aufgetreten. Deine Daten sind davon nicht betroffen — es wurde nichts gespeichert und nichts verändert.',
          'Wechsle oben zu einer anderen Ansicht oder lade die Seite neu.',
        ]}
        haupt={{ label: 'Seite neu laden', onClick: () => window.location.reload() }}
        neben={{ label: 'Zu den Kontakten', href: '/kontakte' }}
        fokus
      />
      {detail !== undefined ? (
        <Accordion collapsible>
          <AccordionItem value="absturz">
            {/* Progressive Offenlegung (NFR-UX-004): die Ursache ist fuer die
                Administration da, nicht fuer die Nutzerin. */}
            <AccordionHeader>Technische Einzelheiten</AccordionHeader>
            <AccordionPanel>
              <p className={styles.detail}>{detail}</p>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ) : undefined}
    </Card>
  );
}

interface State {
  readonly failed: boolean;
  /** Ursache, ausschliesslich fuer die technische Sicht. */
  readonly detail: string | undefined;
}

export class ScreenErrorBoundary extends Component<{ readonly children: ReactNode }, State> {
  override state: State = { failed: false, detail: undefined };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, detail: error instanceof Error ? `${error.name}: ${error.message}` : String(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // In die Browserkonsole und nirgendwo sonst. Eine Fehlermeldung an einen
    // Dienst zu senden waere eine Datenuebermittlung, die niemand entschieden
    // hat: NFR-PRV-001 verlangt dafuer das Verarbeitungsverzeichnis und eine
    // geklaerte Auftragsverarbeitung, NFR-OBS-001 die Minimierung von PII in
    // der Telemetrie, und Punkt 6 der Security-Baseline verbietet interne
    // Fehlerdetails in Ausgaben. Ein componentStack traegt Dateipfade und
    // Komponentennamen -- unauffaellig genug, um still angefangen zu werden,
    // und genau deshalb steht es hier.
    console.error('Bildschirm abgestürzt', error, info.componentStack);
  }

  /**
   * Der Wechsel auf einen anderen Bildschirm setzt die Grenze zurueck.
   *
   * Ohne das bliebe die Fehlermeldung nach dem Absturz stehen, auch nachdem die
   * Nutzerin auf einen intakten Bildschirm gewechselt ist -- der genannte
   * naechste Schritt ("wechsle zu einer anderen Ansicht") liefe dann ins Leere,
   * und das waere schlimmer als kein Hinweis.
   */
  override componentDidUpdate(previous: { readonly children: ReactNode }): void {
    if (this.state.failed && previous.children !== this.props.children) {
      this.setState({ failed: false, detail: undefined });
    }
  }

  override render(): ReactNode {
    return this.state.failed ? <CrashMessage detail={this.state.detail} /> : this.props.children;
  }
}
